import { CardJobTalent } from '@components/templates/job/Card/CardJobTalent';
import { CardJobTalentSkeleton } from '@components/templates/job/Card/CardJobTalentSkeleton';
import { EmployerHomeJobs } from '@components/templates/homepage/EmployerHomeJobs';
import { HomeJobsSlotState } from '@hooks/features/useHomeJobsSlot';

const SKELETON_COUNT = 3;


// Interface
// ********************************************
export interface HomeJobsSlotProps {
  locale: Locale;
  state: HomeJobsSlotState;
  /** When true, render without the `bg-surface-100` + `container` wrappers — caller controls layout. */
  unwrapped?: boolean;
}


/**
 * @description Context-aware home job slot for /mc (and /).
 *
 * - `employer` variant → the company's top own jobs (delegated to EmployerHomeJobs).
 * - `talent` variant   → public jobs (anon) or personalised recommendations (auth),
 *   both rendered with `CardJobTalent`.
 *
 * Silently renders nothing once a `talent` variant has finished loading with no items.
 */
export const HomeJobsSlot = ({ locale, state, unwrapped = false }: HomeJobsSlotProps) => {
  const body = (() => {
    if (state.variant === 'employer') {
      if (state.isLoading) {
        return (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => `emp-skeleton-${i}`).map((key) => (
              <CardJobTalentSkeleton key={key} />
            ))}
          </div>
        );
      }
      return <EmployerHomeJobs locale={locale} items={state.items} />;
    }

    // talent variant
    if (!state.isLoading && !state.hasItems) return null;

    return (
      <div className="grid auto-rows-auto gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
        {state.isLoading
          ? Array.from({ length: SKELETON_COUNT }, (_, i) => `talent-skeleton-${i}`).map((key) => (
            <CardJobTalentSkeleton key={key} />
          ))
          : state.items.map((job) => <CardJobTalent key={job.id} job={job} locale={locale} />)}
      </div>
    );
  })();

  if (body === null) return null;
  if (unwrapped) return body;

  return (
    <div className="bg-surface-100">
      <div className="container">{body}</div>
    </div>
  );
};
